/**
 * What deserves the stage today.
 *
 * Four sources, and the interesting decision is the first one: three of these
 * ceremonies mark milestones that *already* have a trophy. Rather than firing
 * both — a full-screen performance and, underneath it, a toast making the same
 * announcement in a corner — the ceremony replaces the toast. `announceAwards`
 * asks `ceremonyForAward` first and stays quiet about anything that is about
 * to get a room of its own.
 *
 * The fourth is the end of one of her running bits, which has no trophy and no
 * toast: it is the only thing in the app that happens purely because a story
 * finished.
 */

import { anniversaryYears } from '@gramas/shared'
import { momentSeen } from '../glitime/moments.js'
import { glitimeMetAt } from '../glitime/store.js'
import { type CeremonyCopy, DAYS_365, LOG_1000, METINE, SAGA_FINALE, STREAK_100 } from './copy.js'
import { offerCeremony } from './store.js'

/**
 * Awards big enough to be performed rather than remarked on.
 *
 * Everything else in the cabinet stays a toast — that is the right size for
 * "your hundredth entry", and making all eighteen of them ceremonies would
 * make none of them one.
 */
const BY_AWARD: Readonly<Record<string, CeremonyCopy>> = {
  'trophy.streak-100': STREAK_100,
  'beat.days-365': DAYS_365,
  'trophy.log-1000': LOG_1000,
}

/** The ceremony this award earns instead of a toast, if any. */
export function ceremonyForAward(awardKey: string): CeremonyCopy | undefined {
  return BY_AWARD[awardKey]
}

/**
 * Offer the performance an award has just earned.
 *
 * The award row is already granted by the time this runs, so the ceremony is
 * recorded separately: an award marks that the milestone happened, a moment
 * marks that it was *watched*. Closing the app mid-ceremony leaves the second
 * unrecorded, and it is offered again next launch — which is the behaviour you
 * want from something you only get once.
 */
export async function offerForAwards(keys: readonly string[]): Promise<void> {
  for (const key of keys) {
    const copy = ceremonyForAward(key)
    if (copy == null) continue
    if (await momentSeen('ceremony', key)) continue
    offerCeremony({ kind: 'ceremony', id: key, copy })
    return
  }
}

/**
 * The two that are not about an award: the anniversary of the first meeting,
 * and a saga reaching its last chapter.
 *
 * Called on app open with what the presence controller already worked out, so
 * neither costs a query of its own.
 */
export async function offerForToday(input: {
  bigDay: string | null
  sagaFinale: string | null
}): Promise<void> {
  if (input.bigDay === 'metine') {
    const metAt = await glitimeMetAt()
    // Keyed by year: the anniversary comes round again, and the second one is
    // a different occasion from the first.
    const id = `metine-${anniversaryYears(new Date(), metAt)}`
    if (!(await momentSeen('ceremony', id))) {
      offerCeremony({ kind: 'ceremony', id, copy: METINE })
      return
    }
  }

  if (input.sagaFinale != null) {
    // `saga.kempine.4` → `saga.kempine`: the file records the bit, not the
    // chapter, so the dossier can list it as one closed case.
    const sagaId = input.sagaFinale.slice(0, input.sagaFinale.lastIndexOf('.'))
    const copy = SAGA_FINALE[sagaId]
    if (copy != null && !(await momentSeen('saga', sagaId))) {
      offerCeremony({ kind: 'saga', id: sagaId, copy })
    }
  }
}
